import { ContentContext, LocaleContext } from 'Components/hooks/context';
import React, { useContext, useEffect } from 'react';

import Link from 'next/link';
import classnames from 'classnames';
import { component } from './styles.module.scss';
import d3 from 'Utils/d3';
import random from 'lodash/random';
import range from 'lodash/range';
import useObservable from 'Components/hooks/observed';

const Logo = ({ showAnimation = true, withoutLink = false }) => {
  const locale = useContext(LocaleContext);
  const { common } = useContext(ContentContext);
  const content = common.title;
  const chartContainer = React.createRef();

  const isVisible = useObservable({ ref: chartContainer, rootMargin: '600px' });

  // Draw chart
  useEffect(() => {
    const width = 100;
    const height = 50;

    const svg = d3.select(chartContainer.current)
      .appendSelect('svg')
      .attr('height', height)
      .attr('width', width);
    if (!showAnimation) return;

    const x = d3.scaleLinear()
      .domain([0, 2])
      .range([0, width]);

    const y = d3.scaleLinear()
      .domain([0, 1])
      .range([height - 20, 0]);

    const opacity = d3.scaleLinear()
      .domain([0, 1])
      .range([0.1, 0.75]);

    const color = d3.scaleThreshold()
      .domain([0.6, 0.8])
      .range(['#ccc', '#f68e26', '#de2d26']);

    const line = d3.line()
      .curve(d3.curveMonotoneX)
      .x(d => x(d.x))
      .y(d => y(d.y));

    const N = 10;

    // const nullData = range(N).map((i) => ([
    //   { x: 0, y: 0 },
    //   { x: 0.5, y: 0 },
    //   { x: 1, y: 0 },
    //   { x: 1.5, y: 0 },
    //   { x: 2, y: 0 },
    // ]));

    const getRealData = (max = 1) => range(N).map((i) => {
      const y = random(max, true);
      return [
        { x: 0, y: 0 },
        { x: 0.5, y: y / 4 },
        { x: 1, y },
        { x: 1.5, y: y / 4 },
        { x: 2, y: 0 },
      ];
    });

    const draw = (data) => {
      svg.selectAll('path')
        .data(data, (d, i) => i)
        .join(
          enter => enter.append('path')
            .style('stroke', color.range()[0])
            .style('opacity', 0)
            .attr('d', line),
          update => update
            .call(update =>
              update.transition(
                d3.transition().duration(2000)
              )
                .delay((d, i) => i * (2000 / N))
                .style('stroke', d => color(d[2].y))
                .style('opacity', d => opacity(d[2].y))
                .attr('d', line))
        );
    };

    draw(getRealData(1));

    let timer;
    let i = 0;

    if (isVisible) {
      timer = d3.interval(() => {
        if (i % 2 === 0) {
          draw(getRealData(0.5));
        } else {
          draw(getRealData(1));
        }
        i += 1;
      }, 2000);
    } else {
      if (timer) timer.stop();
    }
    return () => {
      if (timer) timer.stop();
    };
  }, [isVisible, chartContainer, showAnimation]);

  const HomeLink = (props) => locale === 'en' ? (
    <Link href='/' as='/'>
      {props.children}
    </Link>
  ) : (
    <Link href='/[locale]' as={`/${locale}`}>
      {props.children}
    </Link>
  );

  const Title = () => (
    <>
      <h1 className='logo-title'>{content.covid}</h1>
      <h4 className='logo-subtitle'>{content.global}</h4>
    </>
  );

  return (
    <div className={classnames(component)}>
      <div ref={chartContainer} />
      {
        withoutLink ? (
          <Title />
        ) : (
          <HomeLink>
            <a>
              <Title />
            </a>
          </HomeLink>
        )
      }
    </div>
  );
};

export default React.memo(Logo);
